import dayjs from 'dayjs';

import { LOCALE_EN_US } from 'src/apps/dashboard/constants/Dashboard';
import { DailyUniqueIssuesData } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';
import { DATE_FORMAT_CATEGORY, DATE_FORMAT_FULL } from 'src/common/consts/DateConsts';

import { CHART_CONSTANTS } from '../constants';
import { ChartData, ChartDataPoint, ChartSeries, CriticalIssuesData } from '../types';

export const prepareDataForCriticalIssuesChart = (data: CriticalIssuesData[]): ChartData => {
	if (!data || data.length === 0) {
		return { categories: [], series: [] };
	}

	// Sort data by date to ensure proper chronological order
	const sortedData = [...data].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

	const categories = sortedData.map((item) => {
		return dayjs(item.date).format(DATE_FORMAT_CATEGORY);
	});

	// Create data points for total count (critical + serious)
	const chartDataPoints: ChartDataPoint[] = sortedData.map((item, index) => {
		const date = dayjs(item.date);
		const formattedDate = date.format(DATE_FORMAT_FULL);
		const tooltipDate = date.format(DATE_FORMAT_CATEGORY);
		const formatter = new Intl.NumberFormat(LOCALE_EN_US);
		const criticalAndSerious = item.critical + item.serious;

		return {
			x: index,
			y: item.critical + item.serious,
			name: `Total Issues: ${item.total} (Critical: ${item.critical}, Serious: ${item.serious}) - ${formattedDate}`,
			date: tooltipDate, // Use formatted date for tooltip
			count: formatter.format(item.total),
			critical: formatter.format(item.critical),
			serious: formatter.format(item.serious),
			total: formatter.format(criticalAndSerious),
			// Use raw numeric values for sorting (table will sort these numerically)
			criticalSortValue: item.critical,
			seriousSortValue: item.serious,
			totalSortValue: item.total,
			id: `critical-issues-${item.date}-${criticalAndSerious}` // Add unique ID for table rows
		};
	});

	const series: ChartSeries[] = [
		{
			type: 'line',
			name: CHART_CONSTANTS.SERIES.NAME,
			data: chartDataPoints,
			color: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
			lineWidth: CHART_CONSTANTS.LINE.WIDTH,
			marker: {
				enabled: chartDataPoints.length === 1,
				fillColor: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
				lineColor: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
				lineWidth: 2,
				radius: CHART_CONSTANTS.LINE.MARKER_RADIUS
			}
		}
	];

	return {
		categories,
		series
	};
};

export const prepareDataForUniqueIssuesChart = (data: DailyUniqueIssuesData[]): ChartData => {
	if (!data || data.length === 0) {
		return { categories: [], series: [] };
	}

	// Sort data by date to ensure proper chronological order
	const sortedData = [...data].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

	const categories = sortedData.map((item) => {
		return dayjs(item.date).format(DATE_FORMAT_CATEGORY);
	});

	const formatter = new Intl.NumberFormat('en-US');

	// Create data points for unique critical + serious issues (one line)
	const chartDataPoints: ChartDataPoint[] = sortedData.map((item, index) => {
		const date = dayjs(item.date);
		const formattedDate = date.format(DATE_FORMAT_FULL);
		const tooltipDate = date.format(DATE_FORMAT_CATEGORY);
		// Calculate critical + serious for the chart line
		const criticalAndSerious = item.critical + item.serious;

		return {
			x: index,
			y: criticalAndSerious,
			name: `Unique Issues: ${criticalAndSerious} (Critical: ${item.critical}, Serious: ${item.serious}) - ${formattedDate}`,
			date: tooltipDate,
			count: formatter.format(criticalAndSerious),
			critical: formatter.format(item.critical),
			serious: formatter.format(item.serious),
			total: formatter.format(item.total), // Keep total for table view
			criticalSortValue: item.critical,
			seriousSortValue: item.serious,
			totalSortValue: item.total,
			id: `unique-issues-${item.date}-${criticalAndSerious}`
		};
	});

	const series: ChartSeries[] = [
		{
			type: 'line',
			name: CHART_CONSTANTS.SERIES.NAME,
			data: chartDataPoints,
			color: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
			lineWidth: CHART_CONSTANTS.LINE.WIDTH,
			marker: {
				enabled: chartDataPoints.length === 1,
				fillColor: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
				lineColor: CHART_CONSTANTS.COLORS.PRIMARY_LINE,
				lineWidth: 2,
				radius: CHART_CONSTANTS.LINE.MARKER_RADIUS
			}
		}
	];

	return {
		categories,
		series
	};
};
